import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { TracingDifficulty, MEDIUM_OUTLINE_FADE_MS } from '../utils';
import * as S from './styles';

interface Props {
  character: string;
  difficulty: TracingDifficulty;
  onComplete: (accuracy: number) => void;
  disabled: boolean;
}

const STROKE_WIDTH = 8;
const CANVAS_SIZE = 280;
const MIN_STROKE_PIXELS = 50;

const calculateAccuracy = (
  drawingCanvas: HTMLCanvasElement,
  referenceCanvas: HTMLCanvasElement,
): number => {
  const drawCtx = drawingCanvas.getContext('2d');
  const refCtx = referenceCanvas.getContext('2d');
  if (!drawCtx || !refCtx) return 0;

  const drawData = drawCtx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;
  const refData = refCtx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;

  let refPixels = 0;
  let overlapPixels = 0;
  let drawnPixels = 0;

  for (let i = 3; i < refData.length; i += 4) {
    if (refData[i] > 50) refPixels++;
    if (drawData[i] > 50) drawnPixels++;
    if (refData[i] > 50 && drawData[i] > 50) overlapPixels++;
  }

  if (refPixels === 0 || drawnPixels < MIN_STROKE_PIXELS) return 0;

  const coverage = overlapPixels / refPixels;
  const precision = drawnPixels > 0 ? overlapPixels / drawnPixels : 0;

  return (coverage + precision) / 2;
};

const createReferenceCanvas = (character: string): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.font = '200px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'black';
    ctx.fillText(character, CANVAS_SIZE / 2, CANVAS_SIZE / 2);
  }
  return canvas;
};

export const CanvasTracer: FC<Props> = ({ character, difficulty, onComplete, disabled }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const referenceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const hasDrawnRef = useRef(false);
  const [outlineOpacity, setOutlineOpacity] = useState(difficulty === 'hard' ? 0 : 1);

  useEffect(() => {
    referenceCanvasRef.current = createReferenceCanvas(character);
  }, [character]);

  useEffect(() => {
    if (difficulty === 'medium') {
      const timer = setTimeout(() => {
        setOutlineOpacity(0);
      }, MEDIUM_OUTLINE_FADE_MS);
      return () => clearTimeout(timer);
    }
    if (difficulty === 'hard') {
      setOutlineOpacity(0);
    }
    return undefined;
  }, [difficulty]);

  const getCanvasPoint = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * CANVAS_SIZE,
        y: ((e.clientY - rect.top) / rect.height) * CANVAS_SIZE,
      };
    },
    [],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      isDrawingRef.current = true;
      const point = getCanvasPoint(e);
      lastPointRef.current = point;
      hasDrawnRef.current = true;
      const target = e.target as HTMLCanvasElement;
      if (target.setPointerCapture) {
        target.setPointerCapture(e.pointerId);
      }
    },
    [disabled, getCanvasPoint],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current || disabled) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx || !lastPointRef.current) return;

      const point = getCanvasPoint(e);
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(point.x, point.y);
      ctx.strokeStyle = '#333333';
      ctx.lineWidth = STROKE_WIDTH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      lastPointRef.current = point;
    },
    [disabled, getCanvasPoint],
  );

  const handlePointerUp = useCallback(() => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    lastPointRef.current = null;

    if (hasDrawnRef.current && canvasRef.current && referenceCanvasRef.current) {
      const accuracy = calculateAccuracy(canvasRef.current, referenceCanvasRef.current);
      if (accuracy > 0.3) {
        onComplete(accuracy);
      }
    }
  }, [onComplete]);

  const resetCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    hasDrawnRef.current = false;
  }, []);

  useEffect(() => {
    resetCanvas();
    setOutlineOpacity(difficulty === 'hard' ? 0 : 1);
  }, [character, resetCanvas, difficulty]);

  return (
    <S.Container data-testid="canvas-tracer">
      <S.ReferenceCharacter $opacity={outlineOpacity}>{character}</S.ReferenceCharacter>
      <S.Canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
    </S.Container>
  );
};
